import { combineLatest, Observable, tap, debounceTime, map } from 'rxjs';
import { ViewpointRelationshipsViewSourceData } from '../types';
import type { ViewSettingsForModelSourceData } from './viewSettingsPartitions';
import { HasViewInstanceId } from '@ardoq/graph';
import {
  APIOrganizationUser,
  ArdoqId,
  GroupingRule,
  ViewIds,
} from '@ardoq/api-types';
import {
  allActiveOrgUsers$,
  globalViewModelResetSource$,
  orgUsersById$,
} from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import type { ViewStatusReporter } from 'tabview/activeViewsStatus/types';
import { reportDataWorkStarted } from 'tabview/activeViewsStatus/viewStatusReporter';
import {
  action$,
  combineReducers,
  dispatchAction,
  withNamespace,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  DEFAULT_EXPECTED_DURATION,
  startViewLoadTracking,
} from 'tracking/durationTracking/viewPerformance';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { componentsChanged$ } from 'streams/components/components$';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import { getComponentIdsFromViewModel } from 'tabview/relationshipDiagrams/utils';
import { setStateProperty } from '@ardoq/common-helpers';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { GlobalRelationshipsViewModelResetStreamShape } from 'tabview/relationshipDiagrams/viewModel$/types';
import { handleViewpointComponentsUpdate } from 'tabview/relationshipDiagrams/viewModel$/handleViewpointComponentUpdate';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import type { I18nLocale } from '@ardoq/i18n';
import { FormattingAndLabelRules } from '@ardoq/data-model';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import {
  buildViewpointModeSourceData,
  getEmptyViewpointModeSourceData,
  type BuildViewpointModeSourceDataArgs,
} from './buildViewpointModeSourceData';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;

type BaseResetCombineLatestTuple = [
  GlobalRelationshipsViewModelResetStreamShape,
  ViewSettingsForModelSourceData,
  APIOrganizationUser[],
  FormattingAndLabelRules,
  GroupingRule[],
  I18nLocale,
  Record<ArdoqId, APIOrganizationUser>,
];
const toBaseResetPayload = ([
  globalAppState,
  viewSettings,
  users,
  { rules: formattingAndLabelRules },
  groupingRules,
  i18nLocale,
  orgUsersById,
]: BaseResetCombineLatestTuple): BuildViewpointModeSourceDataArgs => {
  const [loadedScenarioData, scenarioRelatedComponents, { showScopeRelated }] =
    globalAppState.scenario;
  return {
    context: globalAppState.context,
    loadedGraph: globalAppState.loadedGraph,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    performanceTrackingId: null,
    viewSettings,
    users,
    formattingAndLabelRules,
    groupingRules,
    i18nLocale,
    orgUsersById,
  };
};

const startTracking = (
  args: BuildViewpointModeSourceDataArgs
): BuildViewpointModeSourceDataArgs => ({
  ...args,
  performanceTrackingId: startViewLoadTracking(
    viewIdToUserFriendlyName[VIEW_ID],
    DEFAULT_EXPECTED_DURATION
  ),
});

export type GetViewpointModeSourceDataArgs = HasViewInstanceId & {
  viewSettingsForModelSourceData$: Observable<ViewSettingsForModelSourceData>;
  viewStatusReporter: ViewStatusReporter;
};
export const getViewpointModeSourceData$ = ({
  viewInstanceId,
  viewSettingsForModelSourceData$,
  viewStatusReporter,
}: GetViewpointModeSourceDataArgs): Observable<ViewpointRelationshipsViewSourceData> => {
  const reset$ = combineLatest([
    globalViewModelResetSource$,
    viewSettingsForModelSourceData$,
    allActiveOrgUsers$,
    formattingAndLabelRules$,
    groupingRules$.pipe(
      map(({ groupingRules }) =>
        groupingRules.map(groupingRuleInterface.toInterfaceType)
      )
    ),
    i18nLocale$,
    orgUsersById$,
  ]).pipe(
    map(toBaseResetPayload),
    map(startTracking),
    reportDataWorkStarted(viewStatusReporter, 'reset')
  );

  const emptyState = getEmptyViewpointModeSourceData(viewInstanceId);

  return action$.pipe(
    withNamespace(viewInstanceId),
    combineReducers(emptyState, [
      streamReducer(reset$, buildViewpointModeSourceData),
      streamReducer(componentsChanged$, handleViewpointComponentsUpdate),
      streamReducer(scopeDiff$, setStateProperty('scopeDiffState')),
    ]),
    debounceTime(2 * DEFAULT_DEBOUNCE_TIME),
    tap(viewModel => {
      dispatchAction(
        readViewComponentIds({
          [VIEW_ID]: getComponentIdsFromViewModel(viewModel),
        })
      );
    })
  );
};
